import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { UrlService } from 'app/core/http/url.service';
import { stringWrapper } from 'app/core/kh-utils/kh-utils';
import { RegionInfo } from 'app/shared/models/region-code.model';
import { GameShowReportModel } from 'app/teacher/mission-reports/game-show-report/models/game-show-report.model';
import { ClassModel } from 'app/teacher/models/class.model';
import { ClassCreatingResponseModel } from 'app/teacher/models/class-creating-response.model';
import { ClassInfo, UpdatedClassInfo } from 'app/teacher/models/class-info';
import { ExistingContentModel } from 'app/teacher/models/existing-content.model';
import { GrowthReportModel } from 'app/teacher/models/growth-report.model';
import { GrowthReportQuestionInfoModel } from 'app/teacher/models/growth-report-question-info.model';
import { GrowthReportStudentAttemptModel } from 'app/teacher/models/growth-report-student-attempt.model';
import { LoginTokenWithClassModel } from 'app/teacher/models/login-token-with-class.model';
import { MissionReportModel } from 'app/teacher/models/mission-report.model';
import { ParsedStudentNamesResponseModel } from 'app/teacher/models/parsed-student-names-response.model';
import { RegisterMathologyDataModel } from 'app/teacher/models/register-mathology-data.model';
import { TeacherClassesDetailModel } from 'app/teacher/models/teacher-classes-detail.model';
import { TeacherClassesListItemModel } from 'app/teacher/models/teacher-classes-list-item.model';
import { UnviewedActionItemCounters } from 'app/teacher/models/unviewed-action-item-counters';
import { MissionModel } from '../models/mission.model';
import { GrowthComparisonModel } from '../models/growth-comparison-model';

@Injectable({
  providedIn: 'root'
})
export class TeacherDataService {
  public constructor(
    public readonly httpClient: HttpClient,
    public readonly urlService: UrlService) { }

  public getUnviewedActionItemCounters(classId: string): Observable<UnviewedActionItemCounters> {
    const url = this.urlService.buildServerApiUrl('Teacher/ActionItems', classId, 'UnviewedAction');

    return this.httpClient.get<UnviewedActionItemCounters>(url);
  }

  public getTeacherClasses(): Observable<ClassInfo[]> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'Classes');

    return this.httpClient.get<ClassInfo[]>(url);
  }

  public getClassInfo(classId: string): Observable<ClassInfo> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'Class', classId);

    return this.httpClient.get<ClassInfo>(url);
  }

  public archiveActivity(activityId: string): Observable<any> {
    const url = this.urlService.buildServerApiUrl('Teacher/Activity', activityId);

    return this.httpClient.delete<any>(url);
  }

  public registerForMathology(data: RegisterMathologyDataModel): Observable<LoginTokenWithClassModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/Registration/Partner');

    return this.httpClient.post<LoginTokenWithClassModel>(url, data);
  }

  public getMissionReport(classId: string, missionId: string): Observable<MissionReportModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/Class', classId, 'Mission', missionId, 'Report');

    return this.httpClient.get<MissionReportModel>(url);
  }

  public getDynamicMissionReport(classId: string, missionId: string): Observable<MissionReportModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/Class', classId, 'DynamicMission', missionId, 'Report');

    return this.httpClient.get<MissionReportModel>(url);
  }

  public getGrowthReport(baseActivityId: string): Observable<GrowthReportModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/Activity', baseActivityId, 'Growth');

    return this.httpClient.get<GrowthReportModel>(url);
  }

    public getGrowthComparison(baseActivityId: string, fromActivityId: string, toActivityId: string): Observable<GrowthComparisonModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/Activity', baseActivityId, 'Growth', 'Compare', fromActivityId, toActivityId);

    return this.httpClient.get<GrowthComparisonModel>(url);
  }

  public getGameShowReport(gameShowRunId: string): Observable<GameShowReportModel> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowReport', gameShowRunId);

    return this.httpClient.get<GameShowReportModel>(url);
  }

  public getGrowthReportQuestion(baseActivityId: string, questionBucketId: string): Observable<GrowthReportQuestionInfoModel[]> {
    const url = this.urlService.buildServerApiUrl('Teacher/Activity', baseActivityId, 'Growth/Question', questionBucketId);

    return this.httpClient.get<GrowthReportQuestionInfoModel[]>(url);
  }

  public getGrowthReportStudentAttempt(activityId: string, studentId: string, questionBucketId: string):
    Observable<GrowthReportStudentAttemptModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/Activity', activityId, 'Growth/StudentAnswer', studentId, questionBucketId);

    return this.httpClient.get<GrowthReportStudentAttemptModel>(url);
  }

  public getDynamicGrowthReport(baseActivityId: string): Observable<GrowthReportModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/DynamicActivity', baseActivityId, 'Growth');

    return this.httpClient.get<GrowthReportModel>(url);
  }

  public getDynamicReportStudentAttempt(activityId: string, studentId: string, questionBucketId: string):
    Observable<GrowthReportStudentAttemptModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/Activity', activityId, 'Dynamic/StudentAnswer', studentId, questionBucketId);

    return this.httpClient.get<GrowthReportStudentAttemptModel>(url);
  }

  public getDynamicReportQuestion(classId: string, baseActivityId: string, questionBucketId: string):
    Observable<GrowthReportQuestionInfoModel[]> {
    const url = this.urlService.buildServerApiUrl('Teacher', classId, 'Activity', baseActivityId, 'Dynamic/Question', questionBucketId);

    return this.httpClient.get<GrowthReportQuestionInfoModel[]>(url);
  }

  public getTeacherClassesDetail(includeLicense = false): Observable<TeacherClassesListItemModel[]> {
    const url = this.urlService.buildServerApiUrl('Teacher/ClassManagement');

    return this.httpClient.get<TeacherClassesDetailModel>(url, { params: { includeLicense: includeLicense } }).pipe(map(({ Classes }) => Classes));
  }

  public removeClassUpgrade(classId: string): Observable<unknown> {
    const url = this.urlService.buildServerApiUrl('Teacher/Class', classId, 'Upgrade');

    return this.httpClient.delete(url);
  }

  public unarchiveClass(classId: string): Observable<unknown> {
    const url = this.urlService.buildServerApiUrl('Teacher/Class', classId, 'Unarchive');

    return this.httpClient.put(url, {});
  }

  public archiveClass(classId: string): Observable<unknown> {
    const url = this.urlService.buildServerApiUrl('Teacher/Class', classId, 'Archive');

    return this.httpClient.put(url, {});
  }

  public getArchivedClasses(): Observable<ClassInfo[]> {
    const url = this.urlService.buildServerApiUrl('Teacher/ArchivedClasses');

    return this.httpClient.get<ClassInfo[]>(url);
  }

  public getExistingContent(): Observable<ExistingContentModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/ExistingContent');

    return this.httpClient.get<ExistingContentModel>(url);
  }

  public upgradeClass(classId: string, redemptionCode: string): Observable<UpdatedClassInfo> {
    const url = this.urlService.buildServerApiUrl('Teacher/Class', classId, 'UpgradeWithCode');

    return this.httpClient.post<UpdatedClassInfo>(url, stringWrapper(redemptionCode));
  }

  public upgradeClassWithCredit(classId: string): Observable<UpdatedClassInfo> {
    const url = this.urlService.buildServerApiUrl('Teacher/Class', classId, 'UpgradeWithCredit');

    return this.httpClient.post<UpdatedClassInfo>(url, {});
  }

  public verifyAutoUpgradeEmail(schoolEmail: string): Observable<unknown> {
    const url = this.urlService.buildServerApiUrl('Teacher/AutoUpgradeEmailVerification');

    return this.httpClient.post<unknown>(url, stringWrapper(schoolEmail));
  }

  public createClass(classInfo: ClassModel): Observable<ClassCreatingResponseModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/Class');

    return this.httpClient.post<ClassCreatingResponseModel>(url, classInfo);
  }

  public parseStudentNames(studentNames: string): Observable<ParsedStudentNamesResponseModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/ParseStudentNames');

    return this.httpClient.post<ParsedStudentNamesResponseModel>(url, stringWrapper(studentNames));
  }

  public getTeacherRegion(): Observable<RegionInfo> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'Country');

    return this.httpClient.get<RegionInfo>(url);
  }

  public editMission(missionId: string, mission: MissionModel): Observable<MissionModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/Mission', missionId);
    return this.httpClient.put<MissionModel>(url, mission);
  }

  public deleteMission(activityId: string): Observable<any> {
    const url = this.urlService.buildServerApiUrl('Teacher/Mission', activityId);
    return this.httpClient.delete<any>(url);
  }
}
